import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { LSKEY_INITAIL_URL } from './utils/auth';
import * as Sentry from '@sentry/react';
import { env, isDev } from './config';
import { beforeSend, beforeSendTransaction, beforeBreadcrumb } from './sentry/scrub';

Sentry.init({
  dsn: isDev
    ? undefined
    : 'https://6584a6c72ddf101e7499aa0fcf288fb0@o4507803976335360.ingest.de.sentry.io/4507815153565777',
  environment: env,

  integrations: [
    Sentry.browserTracingIntegration(),
    // browserApiErrorsIntegration is on by default. Narrow the wrappers that surface
    // noisy third-party callback errors (chart libs, drag/drop, Reverb) while keeping
    // the high-signal timer + XHR instrumentation.
    Sentry.browserApiErrorsIntegration({
      setTimeout: true,
      setInterval: true,
      requestAnimationFrame: false,
      XMLHttpRequest: true,
      eventTarget: false,
    }),
    // Server failures only. Routine 4xx (401/403/404/409/429) are handled by the app
    // (redirects/toasts) and were double-reporting against the GraphQL/REST capture
    // paths — see src/graphql/client.ts and src/api/client.ts.
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [[500, 599]],
    }),
    // Operation-name visibility on spans. The raw `graphql.document` attribute (query
    // + variables) is stripped in beforeSendTransaction/beforeSend so GraphQL
    // variables never leave the browser.
    Sentry.graphqlClientIntegration({
      endpoints: ['/api/public/api/graphql'],
    }),
    // Disable console breadcrumbs so app console output (which can carry work data) is
    // never attached to events. Errors are still captured directly.
    Sentry.breadcrumbsIntegration({ console: false }),
  ],

  // Errors are always sent at 100%; this only samples performance transactions.
  // Production is sampled down for cost + to bound span volume; non-prod stays full.
  // Dev is inert (no DSN above).
  tracesSampleRate: env === 'production' ? 0.15 : 1.0,

  // tracePropagationTargets is intentionally left at the v10 default (same-origin
  // only) so `sentry-trace`/`baggage` headers are never sent to third parties and
  // cannot break cross-origin CORS.

  ignoreErrors: [
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
    'Failed to fetch',
    'NetworkError when attempting to fetch resource.',
    'Network request failed',
    'Load failed',
    'AbortError',
    'The operation was aborted',
  ],
  denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-(web-)?extension:\/\//i, /\/extensions\//i],

  // SDK-auto PII (IP, cookies, request headers) stays off. The beforeSend hooks below
  // scrub app-supplied data, URLs and span attributes on top of this.
  sendDefaultPii: false,
  beforeSend,
  beforeSendTransaction,
  beforeBreadcrumb,

  // Session Replay is intentionally NOT enabled: on-screen text (scenario titles, loss
  // figures, incident detail) is exactly the data we must not exfiltrate. To enable
  // later, add Sentry.replayIntegration() relying on the v10 secure defaults
  // (maskAllText / maskAllInputs / blockAllMedia all default TRUE — never set false)
  // with replaysSessionSampleRate: 0 and replaysOnErrorSampleRate ~0.1, then visually
  // QA masking on a real scenario page before trusting it.
});

const exludedPaths = ['/reset-password', '/auth', '/callback', '/verify'];

(function () {
  const pathname = window.location.pathname;
  const search = window.location.search;
  const hash = window.location.hash;

  if (!exludedPaths.includes(pathname) && pathname !== '/') {
    window.localStorage.setItem(LSKEY_INITAIL_URL, pathname + search + hash);
  }
})();

const MOUNT_NODE = document.getElementById('root') as HTMLElement;

const root = createRoot(MOUNT_NODE);

root.render(<App />);

if (module.hot) {
  module.hot.accept(['./App'], () => {
    const NextApp = require('./App').default;
    root.render(<NextApp />);
  });
}
